/**
 * Menuraq StyleEngine v2
 * Converts responsive CSS style objects into TailwindCSS classNames
 * Handles default values for responsive style objects.
 */

type Primitive = string | number;
type StyleValue = Primitive | Record<string, Primitive>;
type StyleObject = Record<string, StyleValue>;

type Breakpoint =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | `min-${string}`
  | `max-${string}`
  | `default`;

/* --------------------------------------------
 * Tailwind Breakpoint Utilities
 * -------------------------------------------- */

const BASE_BREAKPOINTS = ["sm", "md", "lg", "xl", "2xl"];

function isBreakpoint(key: string): key is Breakpoint {
  return (
    BASE_BREAKPOINTS.includes(key) ||
    key.startsWith("min-") ||
    key.startsWith("max-") ||
    key === "default"
  );
}

function normalizeBreakpoint(bp: Breakpoint): string {
  if (bp === "default") return "";
  return bp.startsWith("min-") || bp.startsWith("max-")
    ? bp.replace("-", ":")
    : `${bp}:`;
}

/* --------------------------------------------
 * CSS → Tailwind Property Mapping
 * -------------------------------------------- */

const PROPERTY_MAP: Record<string, string> = {
  color: "text",
  backgroundColor: "bg",

  padding: "p",
  paddingTop: "pt",
  paddingBottom: "pb",
  paddingLeft: "pl",
  paddingRight: "pr",

  margin: "m",
  marginTop: "mt",
  marginBottom: "mb",
  marginLeft: "ml",
  marginRight: "mr",

  width: "w",
  height: "h",
  minWidth: "min-w",
  maxWidth: "max-w",
  minHeight: "min-h",
  maxHeight: "max-h",

  borderRadius: "rounded",
  borderTopLeftRadius: "rounded-tl",
  borderTopRightRadius: "rounded-tr",
  borderBottomLeftRadius: "rounded-bl",
  borderBottomRightRadius: "rounded-br",

  fontSize: "text",
  fontWeight: "font",
  gap: "gap",

  top: "top",
  bottom: "bottom",
  left: "left",
  right: "right",

  zIndex: "z",
};

/* --------------------------------------------
 * Layout / Display Utilities
 * -------------------------------------------- */

const DISPLAY_UTILS = new Set([
  "block",
  "inline-block",
  "inline",
  "flex",
  "grid",
  "hidden",
]);

const POSITION_UTILS = new Set([
  "static",
  "relative",
  "absolute",
  "fixed",
  "sticky",
]);

const FLEX_DIRECTION_MAP: Record<string, string> = {
  row: "flex-row",
  column: "flex-col",
  "row-reverse": "flex-row-reverse",
  "column-reverse": "flex-col-reverse",
};

const GRID_PROPS = {
  gridTemplateColumns: "grid-cols",
  gridTemplateRows: "grid-rows",
};

/* --------------------------------------------
 * Value → Tailwind Compiler
 * -------------------------------------------- */

function toArbitrary(value: Primitive) {
  return `[${value}]`;
}

function compileProperty(prop: string, value: Primitive): string | null {
  /* DISPLAY */
  if (prop === "display" && DISPLAY_UTILS.has(String(value))) {
    return String(value);
  }

  /* POSITION */
  if (prop === "position" && POSITION_UTILS.has(String(value))) {
    return String(value);
  }

  /* FLEX */
  if (prop === "flexDirection") {
    return FLEX_DIRECTION_MAP[String(value)] || null;
  }

  /* GRID */
  if (prop in GRID_PROPS) {
    return `${GRID_PROPS[prop as keyof typeof GRID_PROPS]}-${value}`;
  }

  /* STANDARD MAPPED PROPS */
  const prefix = PROPERTY_MAP[prop];
  if (!prefix) return null;

  return `${prefix}-${toArbitrary(value)}`;
}

/* --------------------------------------------
 * Core Engine
 * -------------------------------------------- */

export function styleToTailwindClassName(style: StyleObject): {
  style: StyleObject;
  className: string;
} {
  const classNames: string[] = [];
  const cleanedStyle: StyleObject = {};

  for (const prop in style) {
    const value = style[prop];

    /* RESPONSIVE OBJECT */
    if (typeof value === "object" && value !== null) {
      let handled = false;

      // Handle default value first if present
      if ("default" in value) {
        const compiled = compileProperty(prop, value["default"]);
        if (compiled) {
          classNames.push(compiled);
          handled = true;
        }
      }

      // Handle all other breakpoints
      for (const bp in value) {
        if (!isBreakpoint(bp) || bp === "default") continue;

        const compiled = compileProperty(prop, value[bp]);
        if (!compiled) continue;

        classNames.push(`${normalizeBreakpoint(bp)}${compiled}`);
        handled = true;
      }

      if (!handled) {
        cleanedStyle[prop] = value;
      }

      continue;
    }

    /* STATIC VALUE */
    cleanedStyle[prop] = value;
  }

  return {
    style: cleanedStyle,
    className: classNames.join(" "),
  };
}